import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import utils from "../../utils/utils";
import * as md5 from "js-md5";
import { GlobalDataType } from "./types";
import { useStartupData } from "../../modules";
import { PROXY_ACTIVATED_EVENT } from "../../modules/domainGuard";

// 类型定义
interface GlobalDataContextType {
  data: GlobalDataType;
  isInitialized: boolean;
  loadData: (params: {
    url: string;
    cdn?: boolean;
    body?: any;
    options?: any;
  }) => Promise<any>;
  clearData: () => void;
  // 便捷属性
  changeTime: number;
  cacheData: Record<string, any>;
}

interface GlobalDataContextProviderProps {
  children: React.ReactNode;
}

// 创建 Context
const GlobalDataContext = createContext<GlobalDataContextType | null>(null);

// 运行锁映射
const runMap: Record<string, boolean> = {};

// 同步运行函数
export function syncRunning(
  key: string,
  runnable: () => Promise<any>
): Promise<any> {
  return new Promise(resolve => {
    const run = async () => {
      try {
        while (runMap[key]) {
          await utils.sleep(100);
        }
        runMap[key] = true;
        const ret = await runnable();
        resolve(ret);
      } finally {
        delete runMap[key];
      }
    };
    run();
  });
}
// 创建全局数据对象
const createGlobalData = (): GlobalDataType => {
  let changeTime = 0;
  let data: Record<string, any> = {};
  const listeners: Array<(data: GlobalDataType) => void> = [];

  return {
    get changeTime() {
      return changeTime;
    },
    get data() {
      return data;
    },
    isDataInitialized() {
      return Object.keys(data).length > 0;
    },
    addListener(callback: (data: GlobalDataType) => void) {
      listeners.push(callback);
    },
    removeListener(callback: (data: GlobalDataType) => void) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    },
    // 内部方法用于更新数据
    _updateData(newData: Record<string, any>) {
      data = { ...data, ...newData };
      changeTime = utils.getServerTime();
      listeners.forEach(listener => listener(this));
    },
  };
};

const globalData = createGlobalData();
// Provider 组件
export function GlobalDataContextProvider({
  children,
}: GlobalDataContextProviderProps) {
  const [data, setData] = useState<GlobalDataType>(globalData);
  const [isInitialized, setIsInitialized] = useState(() =>
    globalData.isDataInitialized()
  );

  // 监听数据变化
  useEffect(() => {
    const handleDataChange = (newData: GlobalDataType) => {
      setData(newData);
      setIsInitialized(true);
    };

    globalData.addListener(handleDataChange);

    return () => {
      globalData.removeListener(handleDataChange);
    };
  }, []);

  // 在组件顶层调用useStartupData
  const { data: startupData } = useStartupData();

  const loadData = useCallback(
    async ({
      url,
      cdn,
      body,
      options,
    }: {
      url: string;
      cdn?: boolean;
      body?: any;
      options?: any;
    }) => {
      // cdn 请求只依赖 designCacheKey：后端已把它内联进 _config，所以 startup 还没
      // 回来也能先发起（走 CF 边缘）。非 cdn 请求仍按原样等 startup，行为不变。
      const inlinedDesignKey = utils.inlinedDesignCacheKey();
      if (!startupData && !(cdn && inlinedDesignKey)) {
        return;
      }
      // designCacheKey 必须进缓存 key：cdn 请求取的是「某个主题版本」的静态快照，
      // 换了 key 就是另一份数据。不放进 key 的话，主题重发后旧数据会一直命中缓存 ——
      // 而 clearData() 只 bump changeTime、并不删除缓存项（_updateData 是展开合并），
      // 对账逻辑实际上是空操作。
      // 只对 cdn 请求加：非 cdn 请求与主题无关，加了会在 startup 到达时（dk 从
      // undefined 变成有值）把所有已缓存项白白失效一次。
      // dk 为 undefined 时 JSON.stringify 会省略该字段，与改动前的 key 完全一致。
      const dk = cdn
        ? startupData?.designCacheKey || inlinedDesignKey
        : undefined;
      const key = md5.md5(JSON.stringify({ url, body, dk }));
      const r = await syncRunning("lock-" + key, async () => {
        const cachedData = globalData.data[key];
        if (!cachedData) {
          let resp;
          if (cdn) {
            resp = await utils.getAsCache(url, startupData);
          } else {
            if (!url.startsWith("/api")) {
              url = "/api" + url;
            }
            resp = await utils.request(url, body, options);
          }
          globalData._updateData({ [key]: resp.data });
          return resp.data;
        }
        return globalData.data[key];
      });
      return r;
    },
    [data.changeTime, startupData]
  );

  const clearData = useCallback(() => {
    globalData._updateData({});
  }, []);

  // designCacheKey 对账：内联 key 来自 HTML，而 SW 会把首页 HTML 当 app shell 缓存，
  // 所以后台重新发布主题后，内联的 key 可能滞后一轮。startup 返回权威 key 后比对，
  // 不一致说明先前是用旧 key 取的数据 → 改写内联值 + bump changeTime。
  // 生效链路：改写 _config → clearData() bump changeTime → loadData 身份变化 →
  // 消费方 effect 重跑 → 此时算出的缓存 key 里 dk 已是新值 → 未命中 → 真正重拉。
  // （注意 clearData 本身并不删除缓存项，靠的是 key 变了，见上方 dk 的注释。）
  // 改写 _config 后条件不再成立，不会循环。
  useEffect(() => {
    const authoritative = startupData?.designCacheKey;
    if (!authoritative || typeof window === "undefined") return;
    const cfg = (window as any)?._config;
    if (!cfg) return;
    // 只在「确实用过内联 key」且与权威值不一致时才失效重拉。
    // 内联 key 因国家不匹配而被弃用时 inlinedDesignCacheKey() 返回 undefined，
    // 说明先前的请求本来就走的权威链路，无需清缓存。
    const used = utils.inlinedDesignCacheKey();
    if (used && used !== authoritative) {
      cfg.designCacheKey = authoritative;
      clearData();
    }
  }, [startupData?.designCacheKey, clearData]);

  // 域名容灾「软恢复」：主域名运行中失效、代理切到备用域名后，domainGuard 派发该事件。
  // bump changeTime（_updateData 会刷新 changeTime 并通知）→ 所有 useGlobalRemoteList 消费者
  // 的 loadData 重新执行：首屏失败、未写缓存的项真正重新请求、经代理走备用域名成功；
  // 已成功缓存的项命中缓存不重复拉。就地恢复，不整页 reload、不打断游戏。
  //
  // 注意范围：这里只覆盖 useGlobalRemoteList 的消费者（游戏【分类】列表等）。
  // 每个分类下的游戏（category_page）走的是 useGameListPaginated，它不消费 changeTime，
  // 自己单独订阅了本事件（见 hooks/useGameListPaginated.ts）—— 别再以为这一条能兜住它。
  useEffect(() => {
    const onProxyActivated = () => {
      clearData();
    };
    window.addEventListener(PROXY_ACTIVATED_EVENT, onProxyActivated);
    return () => {
      window.removeEventListener(PROXY_ACTIVATED_EVENT, onProxyActivated);
    };
  }, [clearData]);

  const value = useMemo(
    () => ({
      data,
      isInitialized,
      loadData,
      clearData,
      changeTime: data.changeTime,
      cacheData: data.data,
    }),
    [data, isInitialized, loadData, clearData]
  );

  return (
    <GlobalDataContext.Provider value={value}>
      {children}
    </GlobalDataContext.Provider>
  );
}

export default GlobalDataContext;
